import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { DocumentIcon, DocumentTextIcon, PhotoIcon } from "@heroicons/react/24/outline";
import { EmailEntityId, FileEntityId, UserMailboxMessageId } from "@sp-crm/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AdvancedSearchEntityType } from "../../generated/graphql";
import * as filesActions from "../../store/actions/files";
import { fancyAlert } from "../ui/fancy-alert";
import { PrimaryCompactButton } from "../ui/primary-button";
import { SecondaryCompactButton } from "../ui/secondary-button";
import {
    AttachmentAddToRecordState,
    AttachmentPreviewPanel,
} from "./attachment-preview-panel";
import { MailboxAttachmentMeta } from "./use-message-body";

const ADD_TO_RECORD_TOOLTIP = "Save this attachment to the record's Files tab";

interface MessageAttachmentListProps {
    messageId: UserMailboxMessageId;
    entityId: EmailEntityId;
    entityType: AdvancedSearchEntityType;
    attachments: MailboxAttachmentMeta[];
}

interface AttachmentRowProps {
    messageId: UserMailboxMessageId;
    entityId: EmailEntityId;
    entityType: AdvancedSearchEntityType;
    attachment: MailboxAttachmentMeta;
}

export const MessageAttachmentList: React.FC<MessageAttachmentListProps> = props => {
    const { messageId, entityId, entityType, attachments } = props;
    return (
        <div className="mt-3 border-t border-gray-200 pt-2">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Attachments
            </div>
            <ul className="divide-y divide-gray-100">
                {attachments.map(attachment => (
                    <AttachmentRow
                        key={attachment.id}
                        messageId={messageId}
                        entityId={entityId}
                        entityType={entityType}
                        attachment={attachment}
                    />
                ))}
            </ul>
        </div>
    );
};

const AttachmentRow: React.FC<AttachmentRowProps> = props => {
    const { messageId, entityId, entityType, attachment } = props;
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [wasAdded, setWasAdded] = useState(false);
    const isAdded = attachment.savedFileId !== null || wasAdded;
    const displayName = attachment.filename || "(unnamed attachment)";
    const previewUrl = attachmentUrl({
        messageId,
        entityId,
        attachmentId: attachment.id,
        disposition: "inline",
    });
    const downloadUrl = attachmentUrl({
        messageId,
        entityId,
        attachmentId: attachment.id,
        disposition: "attachment",
    });

    const tooLargeTitle = attachment.oversized
        ? "Attachment is too large to preview or download"
        : undefined;

    const trpcClient = trpc.useTRPC();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const addMutation = useMutation({
        ...trpcClient.mailbox.saveAttachmentToEntity.mutationOptions(),
        onSuccess: () => {
            setWasAdded(true);
            filesActions.loadFilesAction(entityId as unknown as FileEntityId, dispatch);
            queryClient.invalidateQueries({
                queryKey: ["getActivityFeed", { entityId }],
            });
            queryClient.invalidateQueries({
                queryKey: trpcClient.mailbox.getMessageBody.queryKey({
                    entityId,
                    id: messageId,
                }),
            });
        },
        onError: async () => {
            await fancyAlert(
                "Add Attachment",
                "Failed to save the attachment to this record.",
                "warning",
            );
        },
    });
    const addState = addToRecordButtonState({
        idleLabel: addToRecordButtonLabel(entityType),
        idleTooltip: ADD_TO_RECORD_TOOLTIP,
        oversized: attachment.oversized,
        oversizedTitle: tooLargeTitle,
        isPending: addMutation.isPending,
    });
    const handleAddClick = () =>
        addMutation.mutate({
            entityId,
            messageId,
            attachmentId: attachment.id,
        });
    const previewAddState: AttachmentAddToRecordState = previewAddToRecordState({
        wasAdded: isAdded,
        isPending: addMutation.isPending,
        idleLabel: addToRecordButtonLabel(entityType),
        idleTooltip: ADD_TO_RECORD_TOOLTIP,
        onAdd: handleAddClick,
    });

    return (
        <li className="flex items-center justify-between py-2 gap-2">
            <div className="flex items-center gap-2 min-w-0">
                {iconForContentType(attachment.contentType)}
                <div className="min-w-0">
                    <div className="text-sm text-gray-800 truncate" title={displayName}>
                        {displayName}
                    </div>
                    <div className="text-xs text-gray-500">
                        {formatBytes(attachment.encodedSize)}
                        {attachment.oversized ? (
                            <span className="ml-1 text-amber-600">
                                · too large to preview
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                <SecondaryCompactButton
                    onClick={() => setIsPreviewOpen(true)}
                    disabled={attachment.oversized}
                    title={tooLargeTitle}>
                    Preview
                </SecondaryCompactButton>
                {attachment.oversized ? (
                    <SecondaryCompactButton disabled title={tooLargeTitle}>
                        Download
                    </SecondaryCompactButton>
                ) : (
                    <a href={downloadUrl}>
                        <SecondaryCompactButton>Download</SecondaryCompactButton>
                    </a>
                )}
                {isAdded ? (
                    <span
                        className="inline-flex items-center gap-1 py-1 px-2 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded"
                        title="Saved to this record">
                        <CheckCircleIcon
                            className="h-4 w-4 text-green-600"
                            aria-hidden="true"
                        />
                        Added
                    </span>
                ) : (
                    <PrimaryCompactButton
                        onClick={handleAddClick}
                        disabled={addState.disabled}
                        title={addState.title}>
                        {addState.label}
                    </PrimaryCompactButton>
                )}
            </div>
            <AttachmentPreviewPanel
                isOpen={isPreviewOpen}
                onDismiss={() => setIsPreviewOpen(false)}
                filename={displayName}
                contentType={attachment.contentType}
                previewUrl={previewUrl}
                downloadUrl={downloadUrl}
                addToRecord={previewAddState}
            />
        </li>
    );
};

function addToRecordButtonLabel(entityType: AdvancedSearchEntityType): string {
    switch (entityType) {
        case AdvancedSearchEntityType.Client:
            return "Add to client";
        case AdvancedSearchEntityType.Community:
            return "Add to community";
        case AdvancedSearchEntityType.ReferenceContact:
            return "Add to contact";
        case AdvancedSearchEntityType.ReferenceBusiness:
            return "Add to organization";
        case AdvancedSearchEntityType.HomeCareLead:
            return "Add to client";
        case AdvancedSearchEntityType.CommunityContact:
        case AdvancedSearchEntityType.Activity:
        case AdvancedSearchEntityType.Invoice:
        case AdvancedSearchEntityType.Task:
        case AdvancedSearchEntityType.Tour:
        case AdvancedSearchEntityType.WorkflowExecution:
            throw new Error(
                `Inbox attachments are not supported for entity type ${entityType}`,
            );
        default: {
            const exhaustiveCheck: never = entityType;
            throw new Error(`Unhandled entity type: ${exhaustiveCheck}`);
        }
    }
}

function addToRecordButtonState(args: {
    idleLabel: string;
    idleTooltip: string;
    oversized: boolean;
    oversizedTitle: string | undefined;
    isPending: boolean;
}): { label: string; title: string | undefined; disabled: boolean } {
    if (args.oversized) {
        return { label: args.idleLabel, title: args.oversizedTitle, disabled: true };
    }
    if (args.isPending) {
        return { label: "Adding…", title: undefined, disabled: true };
    }
    return { label: args.idleLabel, title: args.idleTooltip, disabled: false };
}

function previewAddToRecordState(args: {
    wasAdded: boolean;
    isPending: boolean;
    idleLabel: string;
    idleTooltip: string;
    onAdd: () => void;
}): AttachmentAddToRecordState {
    if (args.wasAdded) {
        return { kind: "added" };
    }
    if (args.isPending) {
        return { kind: "pending" };
    }
    return {
        kind: "idle",
        label: args.idleLabel,
        tooltip: args.idleTooltip,
        onAdd: args.onAdd,
    };
}

function iconForContentType(contentType: string): React.ReactNode {
    if (contentType === "application/pdf") {
        return <DocumentTextIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />;
    }
    if (contentType.startsWith("image/")) {
        return <PhotoIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />;
    }
    return <DocumentIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />;
}

function attachmentUrl(args: {
    messageId: UserMailboxMessageId;
    attachmentId: string;
    entityId: EmailEntityId;
    disposition: "inline" | "attachment";
}): string {
    const params = new URLSearchParams({
        entityId: args.entityId,
        disposition: args.disposition,
    });
    return `/api/mailbox/messages/${args.messageId}/attachments/${args.attachmentId}?${params}`;
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
}
